/** @format */

import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import {parseEntityError} from 'frontier-form';
import {
    COMMENT_FORM_MIMETYPES,
    DESIGN_UPLOAD_MIMETYPES,
    REGEX_DESIGN_FILENAME
} from 'frontier-constants';
import Dropzone from 'react-dropzone';

import {
    artworkThumbPathStore,
    handlePick,
    handleUpload,
    handleConvert,
    rmExt,
    handleSaveUpload
} from './uploading_tools';

/**
 * Custom hook to handle uploading logic
 */
function useUploadHandler(props = {}) {
    const {
        saveFileEndpoint = false,
        onUploadComplete = () => {},
        onAllUploadsComplete = () => {},
        createFileEntity = uploadedFile => {
            return {
                qty: 1000,
                order_id: uploadedFile.orderId,
                customer_filename: uploadedFile.filename.replace(REGEX_DESIGN_FILENAME, ''),
                customer_key: uploadedFile.key,
                customer_filesize: uploadedFile.size,
                customer_mimetype: uploadedFile.mimetype,
                customer_filestack_id: uploadedFile.handle,
                preview_url: uploadedFile.url
            };
        },
        orderId = 0,
        convertFiles = true,
        pickOptions = {},
        acceptedTypes = COMMENT_FORM_MIMETYPES,
        isUploadEnabled = true,
        thumbnailField = 'customer_thumb_key'
    } = props;

    const [dropping, setDropping] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileProgress, setFileProgress] = useState({});

    const handleDragEnter = useCallback(() => {
        setDropping(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDropping(false);
    }, []);

    const handleError = (filenameKey, message) => {
        setFileProgress(prev => {
            if (typeof prev[filenameKey] !== 'undefined') {
                return update(prev, {
                    [filenameKey]: {
                        dropping: {$set: false},
                        uploading: {$set: false},
                        converting: {$set: false},
                        finalizing: {$set: false},
                        error: {$set: message}
                    }
                });
            } else {
                return prev;
            }
        });
    };

    const handleUpdateProgress = (filenameKey, updateTarget) => {
        setFileProgress(prev => {
            let updatedData = {};
            if (typeof prev[filenameKey] !== 'undefined') {
                updatedData = update(prev[filenameKey], updateTarget);
            } else {
                updatedData = update({}, updateTarget);
            }
            return {...prev, [filenameKey]: updatedData};
        });
    };

    const handleProgress = (filenameKey, progress) => {
        const fileProgressUpdate = {
            converting: {$set: false},
            error: {$set: false},
            totalBytes: {$set: progress.totalBytes},
            totalPercent: {$set: progress.totalPercent}
        };
        handleUpdateProgress(filenameKey, fileProgressUpdate);
    };

    const handleUploadFile = async (file, uploadConfigOptions) => {
        let uploadedFile;
        try {
            uploadedFile = await handleUpload(file, uploadConfigOptions, acceptedTypes);
        } catch (e) {
            throw new Error('I could not upload this file. ' + e.message);
        }
        return uploadedFile;
    };

    const handleConvertFile = async (uploadedFileUrl, configUpdate) => {
        let convertedFile;
        try {
            const convertedFileResponse = await handleConvert(uploadedFileUrl, configUpdate);
            convertedFile = convertedFileResponse.body;
        } catch (e) {
            throw new Error('I could not convert this file. ' + e.message);
        }
        return convertedFile;
    };

    const handleSaveFile = async fileEntity => {
        let savedFile;
        try {
            const savedFileResponse = await handleSaveUpload(saveFileEndpoint, fileEntity);
            savedFile = savedFileResponse.body;
            if (typeof savedFile.id === 'undefined') {
                throw new Error('Not your fault, maybe something is wrong with the save url?');
            }
        } catch (e) {
            throw new Error('I could not save this file. ' + e?.response?.body?.message);
        }
        return savedFile;
    };

    const handleProcessFile = async (filenameKey, uploadedFile) => {
        let fileEntity;
        try {
            handleUpdateProgress(filenameKey, {converting: {$set: true}});

            uploadedFile.orderId = orderId;
            fileEntity = createFileEntity(uploadedFile);
            fileEntity[thumbnailField] =
                'artwork_thumbnails/thumb_tgm0LTYQsiKCD36xzxI5_219587_Asset71.jpg.png';

            if (convertFiles === true) {
                try {
                    const convertedFile = await handleConvertFile(uploadedFile.url, {
                        storePath: {$set: artworkThumbPathStore(uploadedFile.key)}
                    });
                    if (typeof convertedFile.key !== 'undefined') {
                        fileEntity[thumbnailField] = convertedFile.key;
                    }
                } catch (e) {
                    // Conversion failed, proceed without it
                }
            }

            handleUpdateProgress(filenameKey, {
                converting: {$set: false},
                finalizing: {$set: true}
            });

            if (saveFileEndpoint !== false) {
                fileEntity = await handleSaveFile(fileEntity);
            }

            setFileProgress(state => update(state, {$unset: [filenameKey]}));
        } catch (e) {
            console.log('catching error', e);
            handleError(filenameKey, e.message);
            return;
        }
        onUploadComplete(fileEntity);
        return fileEntity;
    };

    const handleUploadAndProcessFile = async (filenameKey, file) => {
        try {
            const uploadedFile = await handleUploadFile(file, {
                onProgress: {$set: progress => handleProgress(filenameKey, progress)}
            });
            return await handleProcessFile(filenameKey, uploadedFile);
        } catch (e) {
            handleError(filenameKey, e.message);
            return;
        }
    };

    const handleDrop = files => {
        setDropping(false);
        setUploading(true);

        const pendingUploads = [];

        const newFileProgress = files.reduce((acc, file) => {
            const filenameKey = rmExt(file.name);
            acc[filenameKey] = {
                totalPercent: 1.2,
                converting: false,
                error: false
            };
            return acc;
        }, {});

        setFileProgress(prev => ({...prev, ...newFileProgress}));

        files.forEach(file => {
            const filenameKey = rmExt(file.name);
            pendingUploads.push(handleUploadAndProcessFile(filenameKey, file));
        });

        Promise.all(pendingUploads).then(
            results => {
                setUploading(false);
                if (typeof onAllUploadsComplete === 'function') {
                    onAllUploadsComplete(results);
                }
            },
            e => {
                console.log(e);
            }
        );
    };

    const handleClickAddFiles = async () => {
        const pickOptionsTarget = {...pickOptions};
        const pendingUploads = [];

        pickOptionsTarget.onUploadDone = {
            $set: pickedFilesResponse => {
                const pickedFiles = pickedFilesResponse.filesUploaded;

                const newFileProgress = pickedFiles.reduce((acc, file) => {
                    const filenameKey = rmExt(file.filename);
                    acc[filenameKey] = {
                        totalPercent: 100,
                        converting: true,
                        error: false
                    };
                    return acc;
                }, {});

                setFileProgress(newFileProgress);

                pickedFiles.forEach(file => {
                    const filenameKey = rmExt(file.filename);
                    pendingUploads.push(handleProcessFile(filenameKey, file));
                });

                Promise.all(pendingUploads).then(
                    results => {
                        setUploading(false);
                        if (typeof onAllUploadsComplete === 'function') {
                            onAllUploadsComplete(results);
                        }
                    },
                    e => {
                        console.log(e);
                    }
                );
            }
        };

        await handlePick(pickOptionsTarget);
    };

    const handleCancelUpload = filename => {
        setFileProgress(state => update(state, {$unset: [filename]}));
    };

    return {
        dropping,
        uploading,
        fileProgress,
        handleDragEnter,
        handleDragLeave,
        handleDrop,
        handleClickAddFiles,
        handleCancelUpload,
        handleProcessFile
    };
}

export {useUploadHandler};

/**
 * Handle Dragging and dropping onto stage
 * WRAP ALL DESIGN UPLOADERS WITH THIS!!! THIS IS THE ONE!
 * @param ComponentContainer
 */
export default ComponentContainer => {
    const WithUploadHandler = props => {
        const {
            dropping,
            uploading,
            fileProgress,
            handleDragEnter,
            handleDragLeave,
            handleDrop,
            handleClickAddFiles,
            handleCancelUpload,
            handleProcessFile
        } = useUploadHandler(props);

        if (!props.isUploadEnabled) {
            return (
                <ComponentContainer
                    {...props}
                    dropping={dropping}
                    uploading={uploading}
                    fileProgress={fileProgress}
                />
            );
        }

        return (
            <Dropzone
                style={{position: 'static'}}
                noClick={true}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}>
                {({getRootProps}) => (
                    <div {...getRootProps()}>
                        <ComponentContainer
                            {...props}
                            dropping={dropping}
                            uploading={uploading}
                            fileProgress={fileProgress}
                            onClickAddFiles={handleClickAddFiles}
                            onCancelUpload={handleCancelUpload}
                            handleProcessFile={handleProcessFile}
                        />
                    </div>
                )}
            </Dropzone>
        );
    };

    WithUploadHandler.propTypes = {
        saveFileEndpoint: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
        onUploadComplete: PropTypes.func.isRequired,
        onAllUploadsComplete: PropTypes.func.isRequired,
        createFileEntity: PropTypes.func,
        orderId: PropTypes.number.isRequired,
        convertFiles: PropTypes.bool,
        pickOptions: PropTypes.object,
        isUploadEnabled: PropTypes.bool.isRequired,
        acceptedTypes: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
    };

    WithUploadHandler.defaultProps = {
        saveFileEndpoint: false,
        convertFiles: true,
        orderId: 0,
        pickOptions: {},
        acceptedTypes: COMMENT_FORM_MIMETYPES,
        isUploadEnabled: true,
        thumbnailField: 'customer_thumb_key',
        onUploadComplete: () => {},
        onAllUploadsComplete: () => {},
        createFileEntity: uploadedFile => {
            return {
                qty: 1000,
                order_id: uploadedFile.orderId,
                customer_filename: uploadedFile.filename.replace(REGEX_DESIGN_FILENAME, ''),
                customer_key: uploadedFile.key,
                customer_filesize: uploadedFile.size,
                customer_mimetype: uploadedFile.mimetype,
                customer_filestack_id: uploadedFile.handle
            };
        }
    };

    return WithUploadHandler;
};
